import React from 'react';
import { ChartClickInteraction } from '@components/Widgets/types';
import { AvTooltip } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { uniqBy } from '@fabric-ui/utils';
import { Box, useTheme } from '@mui/material';
import { ResponsiveContainer } from 'recharts';
import { AvComposedWidget } from '../../../../../components/Widgets/AvComposedWidget';
import { ChartTooltip } from '../../../../../components/Widgets/ChartTooltip/ChartTooltip';
import { Orientation } from '../../../constants';
import { getArrowIndication, getComparisonDifference, getTimeRangeDescription } from '../../../CustomDashboardsUtils';
import { getDateFormatFromWidget } from '../../../EditCustomDashboard/utils';
import { useGetDisplayName } from '../../../hooks/useGetDisplayName';
import { ColoringRules, tileTitleSX, TrendTypeWidget } from '../../../types';
import ClickableTitle from './ClickableTitle';

const verticalSx = {
  px: 1,
  flexShrink: 0,
  ...flex.colJustifyAround,
  height: '100%',
};

const horizontalSx = {
  height: '100%',
  width: '100%',
  ...flex.justifyStartCenter,
};

export interface TrendWidgetProps {
  data: any[];
  metric: string;
  orientation?: Orientation;
  trendWidth?: number | string;
  showPercentage?: boolean;
  widget: TrendTypeWidget;
  clickInteractions?: ChartClickInteraction[];
}

const TrendTileWidget = ({
  data,
  metric,
  widget,
  orientation = Orientation.Vertical,
  trendWidth = '100%',
  showPercentage = true,
  clickInteractions,
}: TrendWidgetProps) => {
  const theme = useTheme();
  const {
    select: { dims, metrics },
    timeRange,
    projectionId: { name: projName },
  } = widget.requests[0];
  const { coloringRule } = widget.definition.custom;
  const dateFormat = getDateFormatFromWidget(widget);
  const timeRangeDescription = getTimeRangeDescription(timeRange!);
  const getDisplayName = useGetDisplayName(projName);
  const dataRow = data?.map(row =>
    data
      ?.filter(v => v[dims[0]?.name] === row[dims[0]?.name])
      .reduce(
        (acc, v) => ({
          date: v[dims[0]?.name],
          ...acc,
          ...metrics.reduce((acc, metric) => ({ ...acc, [metric.alias || 0]: v[metric.alias || 0] }), {}),
        }),
        {}
      )
  );
  const lineData = uniqBy(dataRow, v => v.date);
  const series = [{ name: getDisplayName(metric), color: theme.palette.colors.primary[500], type: 'line', dataKey: metric }];
  const currentValue = data[data.length - 1][metric];
  const comparisonDifference = getComparisonDifference(data, metric);
  const isHorizontal = orientation === Orientation.Horizontal;
  return (
    <Box sx={!isHorizontal ? verticalSx : horizontalSx}>
      <Box sx={!isHorizontal ? flex.justifyStartEnd : { ...flex.justifyStartCenter, width: 200 }}>
        {showPercentage && (
          <ClickableTitle
            value={currentValue}
            metricName={metric}
            projName={projName}
            titleSx={tileTitleSX}
            clickInteractions={clickInteractions}
          />
        )}
        {widget.definition.custom?.showChangeIndicator && (
          <Box
            sx={{
              mb: orientation === Orientation.Vertical ? '7px' : undefined,
              ml: orientation === Orientation.Vertical ? 1 : 0,
              color: theme.palette.colors.neutrals[600],
              fontSize: 13,
              fontWeight: 400,
              ...flex.justifyStartCenter,
              flexShrink: 0,
              gap: 1,
            }}>
            <ArrowIndication
              tooltip="Percentage change from start to end of period"
              coloringRule={coloringRule}
              data={data}
              metric={metric}
              isHorizontal={isHorizontal}
            />
            <AvTooltip hasWidthLimit sx={isHorizontal ? { width: comparisonDifference.value === 0 ? 90 : 110 } : {}}>
              <div>{((isHorizontal && comparisonDifference.value !== 0) || !isHorizontal) && timeRangeDescription}</div>
            </AvTooltip>
          </Box>
        )}
      </Box>
      <div />
      <ResponsiveContainer minHeight={0} style={!isHorizontal ? { marginBottom: 50, marginTop: 10 } : undefined} width={trendWidth}>
        <AvComposedWidget
          xAxisKey="date"
          isDate
          data={lineData}
          dateFormat={dateFormat}
          series={series}
          showLegend={false}
          defaultAxisProps={{ x: { hide: true }, y: { hide: true } }}
          showCartesianGridProps={{ vertical: false, horizontal: false }}
          tooltipComponentProps={{ allowEscapeViewBox: { x: false, y: true } }}
          responsiveContainerProps={{ sx: { overflow: 'visible', minHeight: 0 } }}
        />
      </ResponsiveContainer>
    </Box>
  );
};
export default TrendTileWidget;

export const ArrowIndication = ({
  tooltip,
  coloringRule,
  data,
  metric,
  isHorizontal,
  hideNoChange = false,
}: {
  tooltip: string;
  coloringRule: ColoringRules | undefined;
  data: any[];
  metric: string;
  isHorizontal: boolean;
  hideNoChange?: boolean;
}) => {
  const comparisonDifference = getComparisonDifference(data, metric);

  return (
    <AvTooltip
      title={<ChartTooltip titleSlot={tooltip} />}
      muiProps={{
        placement: 'top',
        componentsProps: {
          popper: { sx: { '& .MuiTooltip-tooltip': { p: 0 } } },
        },
      }}>
      {getArrowIndication({ coloringRule, comparisonDifference, showNoChangeImage: isHorizontal, hideNoChange })}
    </AvTooltip>
  );
};
